import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../../../hooks";
import { VirtualScenes } from "../../../../api";
import { useTranslation } from "react-i18next";
import { hasText, hasItems } from "../guiaHelpers";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "./Module6.scss";

const virtualScenesController = new VirtualScenes();

export function Module6({ refId }) {
  const module6 = "module_06";
  const { accessToken } = useAuth();
  const { t } = useTranslation("scenes");

  const [moduleData, setModuleData] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchManyByIds = useCallback(
    async (ids = []) => {
      if (!ids.length) return [];
      const results = await Promise.allSettled(
        ids.map((id) => virtualScenesController.getRepoScene(id, accessToken)),
      );
      return results
        .filter((r) => r.status === "fulfilled" && r.value)
        .map((r) => r.value);
    },
    [accessToken],
  );

  const fetchModuleData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await virtualScenesController.getVirtualScenesModeule(
        refId,
        accessToken,
        module6,
      );
      const md = data?.moduleData || null;
      setModuleData(md);

      const imgs = await fetchManyByIds(md?.ModSixAdditionalMaterials || []);
      setAdditionalImages(imgs);

      const pdfs = await fetchManyByIds(md?.ModSixFilesPdf || []);
      setPdfFiles(pdfs);
    } catch (err) {
      console.error("Error al obtener datos del módulo:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [refId, accessToken, module6, fetchManyByIds]);

  useEffect(() => {
    if (refId && accessToken) {
      fetchModuleData();
    }
  }, [refId, accessToken, fetchModuleData]);

  if (loading || error) return null;

  const sources = moduleData?.ModSixFuentes;
  const hasImages = hasItems(additionalImages);
  const hasPdfs = hasItems(pdfFiles);
  const hasSources = hasText(sources);

  if (!hasImages && !hasPdfs && !hasSources) return null;

  return (
    <div className="module6-container">
      <h2 className="block-title">{t("guide.additionalMaterial")}</h2>

      {hasImages && (
        <div className="module6-carousel">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 10000 }}
            loop={true}
          >
            {additionalImages.map((item, index) => (
              <SwiperSlide key={item._id || item.id || index}>
                <img
                  src={item.url}
                  alt={item.title || `Material adicional ${index + 1}`}
                  className="module6-carousel-image"
                />
                {item.title && <p className="image-title">{item.title}</p>}
                {item.description && (
                  <p className="image-title">{item.description}</p>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Manuales PDF - Sección Rediseñada */}
      {hasPdfs && (
        <>
          <h2 className="block-title">{t("guide.pdfManuals")}</h2>

          <div className="module6-pdf-list">
            {pdfFiles.map((pdf, idx) => (
              <a
                key={pdf._id || pdf.id || idx}
                className="pdf-card"
                href={pdf.url}
                target="_blank"
                rel="noopener noreferrer"
                title={pdf.title || "Descargar PDF"}
              >
                {/* Círculo del botón */}
                <div className="pdf-icon-circle">
                  <svg
                    className="pdf-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    {/* Icono de flecha hacia abajo (Descarga estándar) */}
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                  </svg>
                </div>

                {/* Título visible con limitación de texto */}
                <span className="pdf-title">
                  {pdf.title || `Documento ${idx + 1}`}
                </span>
              </a>
            ))}
          </div>
        </>
      )}

      {hasSources && (
        <p className="module6-sources">
          {t("guide.sources")} {sources}
        </p>
      )}
    </div>
  );
}
